import type {
  OperationsComplaintsResponse,
  OperationsOnTimeRateResponse,
  OperationsProcessingTimeResponse,
  OperationsTotalRequestsResponse,
} from "@/types/analytics-operations";
import type { TenantOperationsTableRow } from "@/types/analytics-tenant";
import type { MetricCardTrend } from "@/components/pages/analytics/front-desk/components/shared/MetricCard";
import type {
  OperationsSummary,
  OperationsSummaryDelta,
} from "@/components/pages/analytics/front-desk/components/operations/types";
import type {
  AdminHotelMetricRow,
  AdminMetricCell,
} from "../types/adminHotelMetricTable";

const TABLE_DELTA_SUFFIX = " from previous period";

type HotelOperationsSnapshot = {
  totalRequests: OperationsTotalRequestsResponse | null;
  processingTime: OperationsProcessingTimeResponse | null;
  onTimeRate: OperationsOnTimeRateResponse | null;
  complaints: OperationsComplaintsResponse | null;
};

function formatMinutesFromSeconds(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds <= 0) {
    return "0m";
  }

  const minutes = Math.floor(seconds / 60);
  const remainderSeconds = Math.round(seconds % 60);

  if (minutes <= 0) {
    return `${remainderSeconds}s`;
  }
  if (remainderSeconds <= 0) {
    return `${minutes}m`;
  }
  return `${minutes}m ${remainderSeconds}s`;
}

function formatOnTimePercent(value: number | null | undefined): string {
  return value != null ? `${Math.round(value)}%` : "—";
}

function formatCount(value: number): string {
  return value.toLocaleString("en-US");
}

function percentChange(current: number, previous: number): number | null {
  if (current === previous) {
    return 0;
  }

  if (previous === 0) {
    return current === 0 ? 0 : 100;
  }

  return Number((((current - previous) / previous) * 100).toFixed(1));
}

function buildSummaryDelta(
  changePct: number | null,
  trend: MetricCardTrend,
): OperationsSummaryDelta | undefined {
  if (changePct == null) {
    return undefined;
  }

  const sign = changePct > 0 ? "+" : "";
  return {
    text: `${sign}${changePct}% from previous period`,
    trend,
  };
}

function toTableDelta(changePct: number | null, trend: MetricCardTrend) {
  if (changePct == null) {
    return {};
  }

  const sign = changePct > 0 ? "+" : "";
  return {
    delta: `${sign}${changePct}%${TABLE_DELTA_SUFFIX}`,
    trend,
  };
}

function buildMetricCell(
  value: string,
  changePct: number | null,
  trend: MetricCardTrend,
): AdminMetricCell {
  return {
    value,
    ...toTableDelta(changePct, trend),
  };
}

export function mapTenantOperationsSummary(
  totalRequests: OperationsTotalRequestsResponse | undefined,
  processingTime: OperationsProcessingTimeResponse | undefined,
  onTimeRate: OperationsOnTimeRateResponse | undefined,
  complaints: OperationsComplaintsResponse | undefined,
): OperationsSummary {
  return {
    totalRequests: totalRequests?.totals.total ?? 0,
    avgProcessingTime:
      processingTime?.totals.humanReadable ??
      formatMinutesFromSeconds(processingTime?.totals.avgSeconds ?? 0),
    onTimePercent:
      onTimeRate?.totals.onTimePercent != null
        ? Math.round(onTimeRate.totals.onTimePercent)
        : null,
    complaintCount: complaints?.totals.complaintCount ?? 0,
  };
}

function snapshotToSummary(
  snapshot: HotelOperationsSnapshot | null,
): OperationsSummary {
  if (!snapshot) {
    return {
      totalRequests: 0,
      avgProcessingTime: "—",
      onTimePercent: null,
      complaintCount: 0,
    };
  }

  return mapTenantOperationsSummary(
    snapshot.totalRequests ?? undefined,
    snapshot.processingTime ?? undefined,
    snapshot.onTimeRate ?? undefined,
    snapshot.complaints ?? undefined,
  );
}

export function applyTenantOperationsKpiDeltas(
  current: OperationsSummary,
  previous: OperationsSummary | null,
): OperationsSummary {
  if (!previous) {
    return current;
  }

  const totalRequestsChange = percentChange(
    current.totalRequests,
    previous.totalRequests,
  );
  const complaintsChange = percentChange(
    current.complaintCount,
    previous.complaintCount,
  );
  const onTimeChange =
    current.onTimePercent != null && previous.onTimePercent != null
      ? percentChange(current.onTimePercent, previous.onTimePercent)
      : null;

  const currentProcessingSeconds = parseProcessingSeconds(
    current.avgProcessingTime,
  );
  const previousProcessingSeconds = parseProcessingSeconds(
    previous.avgProcessingTime,
  );
  const processingChange =
    currentProcessingSeconds != null && previousProcessingSeconds != null
      ? percentChange(currentProcessingSeconds, previousProcessingSeconds)
      : null;

  return {
    ...current,
    deltaTotalRequests: buildSummaryDelta(
      totalRequestsChange,
      totalRequestsChange != null && totalRequestsChange >= 0
        ? "positive"
        : "negative",
    ),
    deltaAvgProcessingTime: buildSummaryDelta(
      processingChange,
      processingChange != null && processingChange <= 0
        ? "positive"
        : "negative",
    ),
    deltaOnTimePercent: buildSummaryDelta(
      onTimeChange,
      onTimeChange != null && onTimeChange >= 0 ? "positive" : "negative",
    ),
    deltaComplaints: buildSummaryDelta(complaintsChange, "negative"),
  };
}

function parseProcessingSeconds(value: string): number | null {
  const [minutesPart, ...afterMinutesParts] = value.split("m");
  const hasMinutes = afterMinutesParts.length > 0;
  const minutes = hasMinutes ? Number(minutesPart.trim()) : 0;
  const remainder = hasMinutes ? afterMinutesParts.join("m") : value;
  const secondsIndex = remainder.indexOf("s");
  const seconds =
    secondsIndex >= 0 ? Number(remainder.slice(0, secondsIndex).trim()) : 0;
  const totalSeconds = minutes * 60 + seconds;

  if (!Number.isFinite(totalSeconds) || totalSeconds <= 0) {
    return value === "0m" ? 0 : null;
  }

  return totalSeconds;
}

export function mapOperationsHotelRows(
  hotels: TenantOperationsTableRow[],
  currentByHotelId: Map<string, HotelOperationsSnapshot | null>,
  previousByHotelId: Map<string, HotelOperationsSnapshot | null>,
  hotelLogosById: Record<string, string | null>,
): AdminHotelMetricRow[] {
  return hotels.map((hotel) => {
    const current = snapshotToSummary(
      currentByHotelId.get(hotel.hotelId) ?? null,
    );
    const previous = snapshotToSummary(
      previousByHotelId.get(hotel.hotelId) ?? null,
    );

    const totalRequestsChange = percentChange(
      current.totalRequests,
      previous.totalRequests,
    );
    const complaintsChange = percentChange(
      current.complaintCount,
      previous.complaintCount,
    );
    const onTimeChange =
      current.onTimePercent != null && previous.onTimePercent != null
        ? percentChange(current.onTimePercent, previous.onTimePercent)
        : null;

    const currentProcessingSeconds = parseProcessingSeconds(
      current.avgProcessingTime,
    );
    const previousProcessingSeconds = parseProcessingSeconds(
      previous.avgProcessingTime,
    );
    const processingChange =
      currentProcessingSeconds != null && previousProcessingSeconds != null
        ? percentChange(currentProcessingSeconds, previousProcessingSeconds)
        : null;

    return {
      id: hotel.hotelId,
      hotelBrand: hotel.hotelName,
      hotelLogoId: hotelLogosById[hotel.hotelId] ?? null,
      metrics: {
        totalRequests: buildMetricCell(
          formatCount(current.totalRequests),
          totalRequestsChange,
          totalRequestsChange != null && totalRequestsChange >= 0
            ? "positive"
            : "negative",
        ),
        avgProcessingTime: buildMetricCell(
          current.avgProcessingTime,
          processingChange,
          processingChange != null && processingChange <= 0
            ? "positive"
            : "negative",
        ),
        completedOnTime: buildMetricCell(
          formatOnTimePercent(current.onTimePercent),
          onTimeChange,
          onTimeChange != null && onTimeChange >= 0 ? "positive" : "negative",
        ),
        complaints: buildMetricCell(
          formatCount(current.complaintCount),
          complaintsChange,
          complaintsChange != null && complaintsChange <= 0
            ? "positive"
            : "negative",
        ),
      },
    };
  });
}

export type { HotelOperationsSnapshot };
